import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import toast from 'react-hot-toast'

// ── LCA Pre-Appointment Checklist ─────────────────────────────
// Notary: sees full checklist, one section at a time
// Admin:  sees summary (X of 4 sections complete)
// Client: sees friendly status message

const SECTIONS = [
  {
    id: 'section1',
    title: 'After Accepting Assignment',
    icon: '📋',
    groups: [
      {
        id: 'review_order',
        title: 'Review the Order',
        items: [
          { id: 'confirm_borrower',      label: 'Confirm borrower name(s)'                                    },
          { id: 'confirm_signing_type',  label: 'Confirm signing type (Refi, Buyer, Seller, HELOC, RON, etc.)' },
          { id: 'confirm_datetime',      label: 'Confirm appointment date/time'                               },
          { id: 'confirm_location',      label: 'Confirm signing location'                                    },
          { id: 'confirm_instructions',  label: 'Confirm special instructions (POA, Trust, name variations, witness needs)' },
        ],
      },
      {
        id: 'review_docs',
        title: 'Review the Documents',
        items: [
          { id: 'download_docs',         label: 'Download all documents'                   },
          { id: 'verify_pages',          label: 'Verify all pages are present'             },
          { id: 'identify_notarizations',label: 'Identify all notarization sections'       },
          { id: 'identify_initials',     label: 'Identify initials/signature pages'        },
          { id: 'identify_lender_forms', label: 'Identify lender-specific forms'           },
          { id: 'identify_scanbacks',    label: 'Identify scanback requirements'           },
          { id: 'identify_shipping',     label: 'Identify shipping instructions'           },
          { id: 'identify_signer_items', label: 'Identify any "signer must bring" items'   },
        ],
      },
      {
        id: 'confirm_borrower_appt',
        title: 'Confirm Borrower Appointment',
        items: [
          { id: 'call_borrower',         label: 'Call borrower within required timeframe'  },
          { id: 'confirm_appt_datetime', label: 'Confirm date/time'                        },
          { id: 'confirm_appt_address',  label: 'Confirm address'                          },
          { id: 'confirm_parking',       label: 'Confirm parking/gate codes'               },
          { id: 'confirm_all_signers',   label: 'Confirm all signers will be present'      },
          { id: 'confirm_valid_id',      label: 'Confirm valid, unexpired ID'              },
          { id: 'confirm_witnesses',     label: 'Confirm witnesses (if required)'          },
          { id: 'confirm_special_needs', label: 'Ask about special needs, pets, or access issues' },
          { id: 'provide_contact',       label: 'Provide your contact number'              },
        ],
      },
    ],
  },
  {
    id: 'section2',
    title: 'Document Preparation',
    icon: '📄',
    groups: [
      {
        id: 'print_docs',
        title: 'Print Documents Correctly',
        items: [
          { id: 'correct_paper',         label: 'Print on correct paper size (letter/legal)'     },
          { id: 'no_cut_off',            label: 'Ensure no pages are cut off'                    },
          { id: 'no_distortion',         label: 'Ensure no shrink-to-fit distortion'             },
          { id: 'clear_barcodes',        label: 'Ensure barcodes are clear'                      },
          { id: 'visible_sig_lines',     label: 'Ensure signature lines are visible'             },
          { id: 'readable_certs',        label: 'Ensure notarial certificates are readable'      },
          { id: 'two_copies',            label: 'Print two copies if required (borrower copy)'   },
        ],
      },
      {
        id: 'pre_tab',
        title: 'Pre-Tab the Package',
        items: [
          { id: 'tab_sig_pages',         label: 'Signature pages'                                },
          { id: 'tab_initial_pages',     label: 'Initial pages'                                  },
          { id: 'tab_notarial_certs',    label: 'Notarial certificate sections'                  },
          { id: 'tab_lender_forms',      label: 'Lender-specific forms'                          },
          { id: 'tab_scanback_pages',    label: 'Scanback pages'                                 },
          { id: 'tab_shipping',          label: 'Shipping instructions'                          },
        ],
      },
      {
        id: 'loose_certs',
        title: 'Prepare Loose Certificates (If Applicable)',
        items: [
          { id: 'acknowledgments',       label: 'Acknowledgments'                                },
          { id: 'jurats',                label: 'Jurats'                                         },
          { id: 'oath_affirmation',      label: 'Oath/Affirmation wording'                       },
          { id: 'copy_certification',    label: 'Copy certification (if allowed)'                },
          { id: 'credible_witness',      label: 'Credible witness forms'                         },
          { id: 'additional_sig_pages',  label: 'Additional signature pages'                     },
        ],
      },
    ],
  },
  {
    id: 'section3',
    title: 'Prep Your Notary Bag',
    icon: '🎒',
    groups: [
      {
        id: 'notary_tools',
        title: 'Notary Tools',
        items: [
          { id: 'notary_stamp',          label: 'Notary stamp + backup stamp'                    },
          { id: 'journal',               label: 'Journal'                                        },
          { id: 'blue_pens',             label: 'Blue pens (multiple)'                           },
          { id: 'black_pens',            label: 'Black pens (if required)'                       },
          { id: 'thumbprint_pad',        label: 'Thumbprint pad (if required)'                   },
          { id: 'sticky_notes',          label: 'Sticky notes'                                   },
          { id: 'highlighters',          label: 'Highlighters'                                   },
          { id: 'binder_clips',          label: 'Binder clips'                                   },
          { id: 'paper_clips',           label: 'Paper clips'                                    },
          { id: 'portable_scanner',      label: 'Portable scanner (if needed)'                   },
        ],
      },
      {
        id: 'technology',
        title: 'Technology',
        items: [
          { id: 'phone_charged',         label: 'Phone fully charged'                            },
          { id: 'portable_charger',      label: 'Portable charger'                               },
          { id: 'hotspot_ready',         label: 'Hotspot ready'                                  },
          { id: 'ron_login_tested',      label: 'RON platform login tested (if applicable)'      },
          { id: 'portal_login_tested',   label: 'Portal login tested'                            },
        ],
      },
    ],
  },
  {
    id: 'section4',
    title: 'Final Check Before Leaving',
    icon: '✅',
    groups: [
      {
        id: 'final_doc_check',
        title: 'Final Document Check',
        items: [
          { id: 'all_docs_printed',      label: 'All documents printed'                          },
          { id: 'all_pages_accounted',   label: 'All pages accounted for'                        },
          { id: 'no_printing_errors',    label: 'No printing errors'                             },
          { id: 'borrower_copy',         label: 'Borrower copy included'                         },
          { id: 'shipping_label',        label: 'Shipping label printed'                         },
          { id: 'envelope_ready',        label: 'Envelope/package ready'                         },
        ],
      },
      {
        id: 'final_logistics',
        title: 'Final Logistics Check',
        items: [
          { id: 'travel_time',           label: 'Confirm travel time'                            },
          { id: 'traffic',               label: 'Confirm traffic'                                },
          { id: 'weather',               label: 'Confirm weather'                                },
          { id: 'parking_confirmed',     label: 'Confirm parking'                                },
          { id: 'gate_codes_confirmed',  label: 'Confirm gate codes'                             },
          { id: 'eta_with_borrower',     label: 'Confirm ETA with borrower (optional but professional)' },
        ],
      },
    ],
  },
]

// ── Helper: flatten all item IDs for a section ────────────────
function getSectionItemIds(section) {
  return section.groups.flatMap(g => g.items.map(i => i.id))
}

// ── Helper: count checked items in a section ──────────────────
function countChecked(section, checked) {
  const ids = getSectionItemIds(section)
  return ids.filter(id => checked[id]).length
}

// ── Helper: total items in a section ─────────────────────────
function countTotal(section) {
  return section.groups.reduce((a, g) => a + g.items.length, 0)
}

// ── Helper: is section complete ──────────────────────────────
function isSectionComplete(section, checked) {
  return countChecked(section, checked) === countTotal(section)
}

// ── SUMMARY HELPERS (for Admin + Client) ─────────────────────
export function getChecklistSummary(checklist) {
  if (!checklist) return { sectionsComplete: 0, totalSections: 4, pct: 0, label: 'Not started' }
  const sectionsComplete = SECTIONS.filter(s => isSectionComplete(s, checklist)).length
  const pct = Math.round((sectionsComplete / SECTIONS.length) * 100)
  const labels = {
    0: 'Not started',
    1: 'Preparation started',
    2: 'Documents prepared',
    3: 'Bag packed — almost ready!!',
    4: 'Fully prepared — ready to go!!',
  }
  return { sectionsComplete, totalSections: 4, pct, label: labels[sectionsComplete] || 'In progress' }
}

// ── ADMIN SUMMARY VIEW ────────────────────────────────────────
export function ChecklistSummaryAdmin({ checklist }) {
  const summary = getChecklistSummary(checklist)
  return (
    <div style={{
      display:        'flex',
      alignItems:     'center',
      gap:            10,
      padding:        '8px 14px',
      background:     '#FAFAF8',
      border:         '1px solid #E8E6DF',
      borderRadius:   8,
      fontSize:       11,
    }}>
      <div style={{ flex:1 }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
          <span style={{ color:'#0D1B3E', fontWeight:600 }}>Pre-Appointment Checklist</span>
          <span style={{ color:'#7A7870' }}>{summary.sectionsComplete}/{summary.totalSections} sections</span>
        </div>
        <div style={{ height:4, background:'#E8E6DF', borderRadius:2, overflow:'hidden' }}>
          <div style={{
            height:     '100%',
            width:      `${summary.pct}%`,
            background: summary.pct === 100 ? '#2D7A4F' : 'linear-gradient(90deg,#9A7A30,#C9A84C)',
            borderRadius: 2,
            transition: 'width 0.4s ease',
          }} />
        </div>
      </div>
      <div style={{
        fontSize:   10,
        color:      summary.pct === 100 ? '#2D7A4F' : '#9A7A30',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}>{summary.pct === 100 ? '✅ Ready' : `${summary.pct}%`}</div>
    </div>
  )
}

// ── CLIENT SUMMARY VIEW ───────────────────────────────────────
export function ChecklistSummaryClient({ checklist }) {
  const summary = getChecklistSummary(checklist)
  if (summary.sectionsComplete === 0) return null
  return (
    <div style={{
      padding:      '8px 16px',
      background:   '#EAF5EE',
      borderTop:    '1px solid rgba(45,122,79,0.2)',
      fontSize:     11,
      color:        '#2D7A4F',
      fontWeight:   500,
      display:      'flex',
      alignItems:   'center',
      gap:          8,
    }}>
      <span>📋</span>
      <span>{summary.label}</span>
      {summary.pct === 100 && <span style={{ marginLeft:'auto', fontWeight:700 }}>✅ Fully Prepared</span>}
    </div>
  )
}

// ── FULL NOTARY CHECKLIST ─────────────────────────────────────
export default function PreAppointmentChecklist({ jobId, initialChecklist, readOnly }) {
  const [checked,        setChecked]        = useState(initialChecklist || {})
  const [currentSection, setCurrentSection] = useState(0)
  const [saving,         setSaving]         = useState(false)
  const [lastSaved,      setLastSaved]      = useState(null)

  // Auto-save when checked changes
  useEffect(() => {
    if (!jobId || readOnly) return
    const timer = setTimeout(async () => {
      setSaving(true)
      try {
        await supabase.from('jobs').update({ checklist: checked }).eq('id', jobId)
        setLastSaved(new Date())
      } catch (err) {
        toast.error('Could not save checklist: ' + err.message)
      } finally {
        setSaving(false)
      }
    }, 800) // debounce 800ms
    return () => clearTimeout(timer)
  }, [checked, jobId, readOnly])

  function toggleItem(itemId) {
    if (readOnly) return
    setChecked(prev => ({ ...prev, [itemId]: !prev[itemId] }))
  }

  const section  = SECTIONS[currentSection]
  const checkedCount = countChecked(section, checked)
  const totalCount   = countTotal(section)
  const sectionDone  = checkedCount === totalCount
  const isLast       = currentSection === SECTIONS.length - 1
  const allDone      = SECTIONS.every(s => isSectionComplete(s, checked))

  return (
    <div style={{
      background:   '#fff',
      border:       '1px solid #E8E6DF',
      borderRadius: 14,
      overflow:     'hidden',
      fontFamily:   'inherit',
    }}>
      {/* ── HEADER ── */}
      <div style={{
        background: 'linear-gradient(135deg,#0D1B3E,#1A3A5E)',
        padding:    '16px 18px',
      }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, color:'#C9A84C', fontWeight:600 }}>
            Pre-Appointment Checklist
          </div>
          <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', letterSpacing:'0.08em' }}>
            {saving ? 'Saving...' : lastSaved ? `Saved ${lastSaved.toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}` : 'Auto-saves as you check'}
          </div>
        </div>

        {/* Section progress dots */}
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {SECTIONS.map((s, i) => {
            const done = isSectionComplete(s, checked)
            const current = i === currentSection
            return (
              <button key={s.id} type="button"
                onClick={() => setCurrentSection(i)}
                title={s.title}
                style={{
                  display:      'flex',
                  alignItems:   'center',
                  gap:          6,
                  background:   current ? 'rgba(201,168,76,0.2)' : 'transparent',
                  border:       `1px solid ${current ? '#C9A84C' : done ? '#2D7A4F' : 'rgba(255,255,255,0.2)'}`,
                  borderRadius: 20,
                  padding:      '4px 10px',
                  cursor:       'pointer',
                  transition:   'all 0.15s',
                }}>
                <div style={{
                  width:        8,
                  height:       8,
                  borderRadius: '50%',
                  background:   done ? '#2D7A4F' : current ? '#C9A84C' : 'rgba(255,255,255,0.3)',
                }} />
                <span style={{ fontSize:10, color: done ? '#5BA882' : current ? '#C9A84C' : 'rgba(255,255,255,0.5)', fontWeight:600, whiteSpace:'nowrap' }}>
                  {s.icon} {s.title}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* ── CURRENT SECTION ── */}
      <div style={{ padding:'16px 18px' }}>
        {/* Section header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:16, color:'#0D1B3E', fontWeight:600 }}>
            {section.icon} {section.title}
          </div>
          <div style={{
            fontSize:   11,
            color:      sectionDone ? '#2D7A4F' : '#9A7A30',
            fontWeight: 600,
          }}>{checkedCount}/{totalCount} {sectionDone ? '✅' : ''}</div>
        </div>

        {/* Slim progress bar */}
        <div style={{ height:3, background:'#E8E6DF', borderRadius:2, overflow:'hidden', marginBottom:16 }}>
          <div style={{
            height:     '100%',
            width:      `${totalCount > 0 ? (checkedCount / totalCount) * 100 : 0}%`,
            background: sectionDone ? '#2D7A4F' : 'linear-gradient(90deg,#9A7A30,#C9A84C)',
            borderRadius: 2,
            transition: 'width 0.3s ease',
          }} />
        </div>

        {/* Groups + Items */}
        {section.groups.map(group => (
          <div key={group.id} style={{ marginBottom:14 }}>
            <div style={{
              fontSize:      10,
              color:         '#B8B5AB',
              letterSpacing: '0.1em',
              fontWeight:    600,
              marginBottom:  8,
              textTransform: 'uppercase',
            }}>{group.title}</div>
            {group.items.map(item => (
              <div key={item.id}
                onClick={() => toggleItem(item.id)}
                style={{
                  display:      'flex',
                  alignItems:   'center',
                  gap:          10,
                  padding:      '8px 10px',
                  borderRadius: 8,
                  cursor:       readOnly ? 'default' : 'pointer',
                  background:   checked[item.id] ? '#EAF5EE' : '#FAFAF8',
                  border:       `1px solid ${checked[item.id] ? 'rgba(45,122,79,0.2)' : '#F0EEE8'}`,
                  marginBottom: 4,
                  transition:   'all 0.15s',
                }}>
                <div style={{
                  width:        18,
                  height:       18,
                  borderRadius: 4,
                  border:       `1.5px solid ${checked[item.id] ? '#2D7A4F' : '#C9C5BA'}`,
                  background:   checked[item.id] ? '#2D7A4F' : '#fff',
                  display:      'flex',
                  alignItems:   'center',
                  justifyContent: 'center',
                  flexShrink:   0,
                  transition:   'all 0.15s',
                }}>
                  {checked[item.id] && <span style={{ color:'#fff', fontSize:11, fontWeight:700, lineHeight:1 }}>✓</span>}
                </div>
                <span style={{
                  fontSize:        12,
                  color:           checked[item.id] ? '#2D7A4F' : '#0D1B3E',
                  textDecoration:  checked[item.id] ? 'line-through' : 'none',
                  opacity:         checked[item.id] ? 0.7 : 1,
                  lineHeight:      1.4,
                  transition:      'all 0.15s',
                }}>{item.label}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* ── NAVIGATION ── */}
      <div style={{
        padding:        '12px 18px',
        background:     '#FAFAF8',
        borderTop:      '1px solid #F0EEE8',
        display:        'flex',
        justifyContent: 'space-between',
        alignItems:     'center',
      }}>
        <button type="button"
          onClick={() => setCurrentSection(i => Math.max(0, i - 1))}
          disabled={currentSection === 0}
          style={{
            fontSize:   12,
            color:      currentSection === 0 ? '#C9C5BA' : '#9A7A30',
            background: 'none',
            border:     'none',
            cursor:     currentSection === 0 ? 'default' : 'pointer',
            fontWeight: 600,
            fontFamily: 'inherit',
          }}>← Previous</button>

        {allDone ? (
          <div style={{ fontSize:12, color:'#2D7A4F', fontWeight:700 }}>
            🎉 All sections complete — you are ready!!
          </div>
        ) : (
          <div style={{ fontSize:11, color:'#B8B5AB' }}>
            Section {currentSection + 1} of {SECTIONS.length}
          </div>
        )}

        {!isLast ? (
          <button type="button"
            onClick={() => setCurrentSection(i => Math.min(SECTIONS.length - 1, i + 1))}
            style={{
              fontSize:   12,
              color:      '#9A7A30',
              background: 'none',
              border:     'none',
              cursor:     'pointer',
              fontWeight: 600,
              fontFamily: 'inherit',
            }}>Next →</button>
        ) : (
          <div style={{ width:60 }} />
        )}
      </div>
    </div>
  )
}
